// 클립보드 공유(설계서 §9.3.1) — **내 PC** 클립보드 읽기·쓰기.
//
// 두 환경을 한 창구로 감싼다.
//  - 통합앱(Tauri): 네이티브 클립보드 플러그인. 권한 창·사용자 제스처 제약이 없다.
//  - 브라우저: navigator.clipboard. 읽기는 권한 창을 띄우고(Chrome), 쓰기는 Firefox·Safari 에서 사용자 동작 직후에만 된다.
//    그래서 붙여넣기(Ctrl+V)는 권한 없이 내용을 받는 **paste 이벤트**를 쓴다(`contentFromDataTransfer`),
//    쓰기가 막히면 `LocalClipboardBlocked` 를 던져 호출측이 "내 PC에 복사" 버튼(=사용자 동작)을 띄우게 한다.
//
// 이미지는 언제나 PNG 로 맞춘다(에이전트와 PNG 로만 주고받는다).
import { IS_SHELL } from '../shell';
import { CLIP_ERR, ClipError, MAX_IMAGE_PIXELS, type ClipContent } from './protocol';

/** 브라우저가 지금은 클립보드 쓰기를 허용하지 않는다 — 사용자가 버튼을 누르면 된다. */
export class LocalClipboardBlocked extends Error {
  constructor() {
    super('브라우저가 클립보드 쓰기를 막았습니다');
    this.name = 'LocalClipboardBlocked';
  }
}

const PNG_TYPE = 'image/png';

/** 이미지 Blob(PNG·JPEG·BMP 등) → PNG 바이트. 이미 PNG 면 그대로. */
export async function blobToPng(blob: Blob): Promise<Uint8Array> {
  if (blob.type === PNG_TYPE) {
    return new Uint8Array(await blob.arrayBuffer());
  }
  const bitmap = await createImageBitmap(blob);
  try {
    return await drawToPng(bitmap.width, bitmap.height, (ctx) => ctx.drawImage(bitmap, 0, 0));
  } finally {
    bitmap.close();
  }
}

/** RGBA 픽셀 → PNG 바이트(앱 플러그인이 픽셀로 준다). */
function rgbaToPng(rgba: Uint8Array, width: number, height: number): Promise<Uint8Array> {
  return drawToPng(width, height, (ctx) =>
    ctx.putImageData(new ImageData(new Uint8ClampedArray(rgba), width, height), 0, 0),
  );
}

async function drawToPng(
  width: number,
  height: number,
  draw: (ctx: CanvasRenderingContext2D) => void,
): Promise<Uint8Array> {
  if (width <= 0 || height <= 0 || width * height > MAX_IMAGE_PIXELS) {
    throw new ClipError(CLIP_ERR.TOO_LARGE);
  }
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new ClipError(CLIP_ERR.FAILED, '이미지를 변환할 수 없습니다');
  }
  draw(ctx);
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, PNG_TYPE));
  if (!blob) {
    throw new ClipError(CLIP_ERR.FAILED, '이미지를 PNG 로 만들 수 없습니다');
  }
  return new Uint8Array(await blob.arrayBuffer());
}

/**
 * paste 이벤트의 데이터에서 **이미지 파일만** 동기로 집어낸다. 없으면 null.
 *
 * 🔴 **동기여야 한다.** 붙여넣기를 가로채려면 `preventDefault()` 를 이벤트 핸들러가 **끝나기 전에** 불러야 하는데,
 *    `await` 뒤에서 부르면 브라우저가 기본 붙여넣기를 이미 마친 뒤다(2026-09-23 실제 발생:
 *    macOS 스크린샷을 붙여넣자 **파일 이름이 입력칸에 들어가 그대로 전송**됐다).
 *    ⇒ "이미지가 있는가" 는 동기로 판정하고, PNG 변환만 비동기로 이어 간다.
 *
 * 🔴 **채팅은 이미지를 텍스트보다 우선한다** — `contentFromDataTransfer`(클립보드 공유)와 **반대**다.
 *    웹 페이지의 이미지를 복사하면 `text/plain` 에 **U+FFFC(객체 대체 문자) + alt 텍스트**가 함께 담긴다.
 *    텍스트를 우선하면 그림 대신 그 글자가 나간다(피제어 화면에 `￼그림설명` 으로 보였다 — 2026-09-23 사용자 보고).
 *    말풍선에 그림을 붙이려던 사람의 뜻은 **그림**이므로 이쪽에서는 이미지를 먼저 본다.
 */
export function imageFileFromDataTransfer(dt: DataTransfer | null): File | null {
  if (!dt) {
    return null;
  }
  for (const item of Array.from(dt.items)) {
    if (item.kind === 'file' && item.type.startsWith('image/')) {
      const file = item.getAsFile();
      if (file) {
        return file;
      }
    }
  }
  return null;
}

/**
 * 내 클립보드 글자 → 보낼 내용. **NFC 로 맞춘다**(2026-10-23 실기: Finder 경로 복사 `…/김병규/…` 가 원격에 `ㄱㅣㅁㅂㅕㅇㄱㅠ` 로 붙었다 —
 * macOS 는 파일·폴더 이름을 NFD(한글 첫가끝 분해)로 주고 Windows 는 그 열을 합쳐 그리지 않는다). 받는 쪽(에이전트)도 합치지만 먼저 맞춘다.
 * 글자를 내 클립보드에서 읽는 모든 경로가 이 함수를 지난다.
 */
function textContent(text: string): ClipContent {
  return { kind: 'text', text: text.normalize('NFC') };
}

/**
 * paste 이벤트의 데이터 → 내용. 텍스트를 우선한다(에이전트와 같은 규칙 — 표를 복사하면 텍스트+그림이 함께 온다).
 * 보낼 것이 없으면 null.
 *
 * ⚠️ 이 규칙은 **클립보드 공유**(§9.3.1)의 것이다. 채팅 첨부는 위 `imageFileFromDataTransfer` 를 쓴다.
 */
export async function contentFromDataTransfer(dt: DataTransfer | null): Promise<ClipContent | null> {
  if (!dt) {
    return null;
  }
  const text = dt.getData('text/plain');
  if (text) {
    return textContent(text);
  }
  for (const item of Array.from(dt.items)) {
    if (item.kind === 'file' && item.type.startsWith('image/')) {
      const file = item.getAsFile();
      if (file) {
        return { kind: 'image', png: await blobToPng(file) };
      }
    }
  }
  return null;
}

/** 내 클립보드를 읽는다(버튼 · 브라우저에서 paste 이벤트가 오지 않을 때). 비어 있으면 null. */
export async function readLocal(): Promise<ClipContent | null> {
  if (IS_SHELL) {
    const cm = await import('@tauri-apps/plugin-clipboard-manager');
    // 플러그인은 해당 형식이 없으면 예외를 던진다 → 텍스트, 이미지 순으로 시도한다.
    const text = await cm.readText().catch(() => '');
    if (text) {
      return textContent(text);
    }
    const image = await cm.readImage().catch(() => null);
    if (image) {
      const [{ width, height }, rgba] = await Promise.all([image.size(), image.rgba()]);
      return { kind: 'image', png: await rgbaToPng(rgba, width, height) };
    }
    return null;
  }

  const clip = navigator.clipboard;
  if (!clip) {
    throw new ClipError(CLIP_ERR.LOCAL_READ);
  }
  try {
    if (typeof clip.read === 'function') {
      const items = await clip.read();
      for (const item of items) {
        if (item.types.includes('text/plain')) {
          const text = await (await item.getType('text/plain')).text();
          if (text) {
            return textContent(text);
          }
        }
      }
      for (const item of items) {
        const type = item.types.find((t) => t.startsWith('image/'));
        if (type) {
          return { kind: 'image', png: await blobToPng(await item.getType(type)) };
        }
      }
      return null;
    }
    const text = await clip.readText();
    return text ? textContent(text) : null;
  } catch (e) {
    if (e instanceof ClipError) {
      throw e;
    }
    throw new ClipError(CLIP_ERR.LOCAL_READ);
  }
}

/** 내 클립보드에 쓴다. 브라우저가 막으면 LocalClipboardBlocked. */
export async function writeLocal(c: ClipContent): Promise<void> {
  if (IS_SHELL) {
    const cm = await import('@tauri-apps/plugin-clipboard-manager');
    if (c.kind === 'text') {
      await cm.writeText(c.text);
    } else {
      await cm.writeImage(c.png);
    }
    return;
  }
  const clip = navigator.clipboard;
  if (!clip) {
    throw new LocalClipboardBlocked();
  }
  try {
    if (c.kind === 'text') {
      await clip.writeText(c.text);
    } else {
      // Uint8Array 를 그대로 Blob 에 넣으면 TS DOM 형식(ArrayBufferLike)과 맞지 않아 사본을 만든다.
      const blob = new Blob([c.png.slice().buffer], { type: PNG_TYPE });
      await clip.write([new ClipboardItem({ [PNG_TYPE]: blob })]);
    }
  } catch (e) {
    // NotAllowedError(사용자 동작 없음·포커스 없음) — 버튼으로 다시 시도하게 한다.
    if (e instanceof DOMException && e.name === 'NotAllowedError') {
      throw new LocalClipboardBlocked();
    }
    throw new ClipError(CLIP_ERR.FAILED, `내 PC 클립보드에 쓰지 못했습니다: ${(e as Error).message}`);
  }
}
